// Conversion CTA -- mirrors the ETF Wealth Builder CTA styling, driven by the
// gap between the visitor's holdings count and their true independent bets.

import React from 'react';

import type { DiversificationResult } from '../types';

interface Props {
    result: DiversificationResult;
}

export const ConversionCTA: React.FC<Props> = ({ result }) => {
    const holdings = result.tickers.length;
    const volGap = result.portfolio_volatility - result.hrp_portfolio_volatility;

    return (
        <div className="mt-2 bg-slate-900 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>

            <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-6 tracking-tight">
                    Are You As Diversified As You Think?
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                        <p className="text-slate-400 text-sm font-medium mb-1">Holdings vs. Independent Bets:</p>
                        <p className="text-2xl font-bold text-rose-400">
                            {holdings} &rarr; {result.effective_number_of_bets.toFixed(1)}
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            You own {holdings} positions, but statistically they behave like only{' '}
                            {result.effective_number_of_bets.toFixed(1)} independent risk sources.
                        </p>
                    </div>

                    <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                        <p className="text-slate-400 text-sm font-medium mb-1">Volatility vs. Risk-Parity Allocation:</p>
                        <p className="text-2xl font-bold text-amber-400">
                            {volGap >= 0 ? '+' : ''}
                            {(volGap * 100).toFixed(2)}% p.a.
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            Difference between your portfolio volatility and the same holdings weighted by
                            Hierarchical Risk Parity.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-center">
                    <p className="text-slate-300 leading-relaxed flex-1 text-sm md:text-base">
                        Hidden correlations quietly concentrate your risk. The{' '}
                        <strong className="text-white">Forge Ultra platform</strong> continuously monitors your
                        portfolios, ranks thousands of assets and builds risk-balanced, multi-asset strategies
                        for you. Upgrade to Premium and unlock institutional portfolio intelligence today.
                    </p>
                    <a
                        href="https://www.ki-wealth.com/membership-levels"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full md:w-auto px-8 py-4 bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold rounded-xl transition-colors shadow-lg shadow-sky-500/30 whitespace-nowrap text-center"
                    >
                        Subscribe and Optimize Your Portfolio
                    </a>
                </div>
            </div>
        </div>
    );
};
